import * as React from 'react';
import { styled } from '@linaria/react';
import { AlertTriangle } from 'lucide-react';

import { range, random, normalize } from '@/utils';
import useSound from '@/hooks/use-sound';

type Particle = {
  id: string;
  angle: number;
  distance: number;
  size: number;
  popDuration: number;
  fadeDuration: number;
  createdAt: number;
};

interface Props {
  isExploded: boolean;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

function ExplodedLine({ isExploded, x1, y1 }: Props) {
  const [showWarning, setShowWarning] = React.useState(false);
  const [particles, setParticles] = React.useState<Array<Particle>>(
    []
  );

  const [play] = useSound('/sounds/pop.mp3');

  React.useEffect(() => {
    if (!isExploded) {
      return;
    }

    const warningTimeoutId = window.setTimeout(() => {
      setShowWarning(true);
    }, 1000);

    play();

    // Generate particles when explosion happens
    const newParticles = range(25).map((index) => {
      const angle = normalize(index, 0, 25, 0, 360) + random(-10, 10);
      const distance = random(5, 55);
      const popDuration =
        normalize(distance, 5, 55, 300, 700) + random(100, 250);
      const fadeDuration =
        normalize(distance, 5, 55, 400, 1400) + random(200, 300);
      const size = random(10, 13);

      return {
        id: crypto.randomUUID(),
        angle,
        distance,
        size,
        popDuration,
        fadeDuration,
        createdAt: Date.now(),
      };
    });

    setParticles(newParticles);

    const particlesCleanupTimeoutId = window.setTimeout(() => {
      setParticles([]);
    }, 3000);

    return () => {
      window.clearTimeout(warningTimeoutId);
      window.clearTimeout(particlesCleanupTimeoutId);
    };
  }, [isExploded, play]);

  return (
    <Wrapper>
      {showWarning && isExploded && (
        <IconWrapper>
          <Icon size={96} strokeWidth={1.5} />
        </IconWrapper>
      )}
      {particles.map((particle) => (
        <Particle
          key={particle.id}
          style={{
            '--angle': particle.angle + 'deg',
            '--distance': particle.distance + 'px',
            '--size': particle.size + 'px',
            '--pop-duration': particle.popDuration + 'ms',
            '--fade-duration': particle.fadeDuration + 'ms',
            '--x': x1 + 'px',
            '--y': y1 + 'px',
          }}
        />
      ))}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const IconWrapper = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  animation: fadeToTransparent 2000ms 4000ms forwards;
`;

const Icon = styled(AlertTriangle)`
  position: absolute;
  inset: 0;
  margin: auto;
  color: var(--wham-primary-500);

  @keyframes fadeIn {
    from {
      opacity: 0.25;
    }
    to {
      opacity: 1;
    }
  }
  animation: fadeIn 480ms ease-in-out 6 alternate forwards;
`;

const Particle = styled.div`
  position: absolute;
  width: var(--size);
  height: var(--size);
  border-radius: 50%;
  background-color: var(--wham-complementary-500);

  @keyframes explode {
    from {
      transform: translate(var(--x), var(--y));
    }
    to {
      transform: translate(
        calc(var(--x) + cos(var(--angle)) * var(--distance)),
        calc(var(--y) + sin(var(--angle)) * var(--distance))
      );
    }
  }

  animation:
    explode var(--pop-duration) var(--particle-curve) forwards,
    fadeToTransparent var(--fade-duration) 500ms forwards;
  --particle-curve: cubic-bezier(0.2, 0.56, 0, 1);
`;

export default ExplodedLine;
